
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, BookOpen, Sparkles, Loader2 } from "lucide-react";
import { useWritingSuggestions } from "@/hooks/useWritingSuggestions";

interface WritingSuggestionsPopoverProps {
  topic: string;
  grade: string;
  writingType: string;
}

const WritingSuggestionsPopover = ({ topic, grade, writingType }: WritingSuggestionsPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<{details: string[], vocabulary: string[]} | null>(null);
  const { getSuggestions, isLoading } = useWritingSuggestions();

  // Load AI suggestions when popover opens
  useEffect(() => {
    if (isOpen && !suggestions && !isLoading) {
      loadSuggestions();
    }
  }, [isOpen]);

  const loadSuggestions = async () => {
    try {
      const aiSuggestions = await getSuggestions({
        topic,
        grade,
        writingType
      });
      setSuggestions(aiSuggestions);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="border-2 border-purple-300 text-purple-700 hover:bg-purple-50"
        >
          <Lightbulb className="h-4 w-4 mr-2" />
          Writing Help
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[600px] p-0 fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 max-h-[80vh]" 
        align="center" 
        side="top"
        avoidCollisions={false}
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 50
        }}
      >
        <div className="max-h-[75vh] overflow-y-auto">
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-purple-50 border-b">
              <CardTitle className="text-purple-700 flex items-center gap-2 text-xl">
                <Sparkles className="h-6 w-6" />
                Writing Suggestions for "{topic}"
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                  <span className="ml-3 text-purple-600 text-lg">Getting helpful suggestions...</span>
                </div>
              ) : suggestions ? (
                <>
                  {/* Topic Details */}
                  <div>
                    <h4 className="font-bold text-blue-700 mb-3 flex items-center gap-2 text-lg">
                      <BookOpen className="h-5 w-5" />
                      What to Include:
                    </h4>
                    <ul className="space-y-3">
                      {suggestions.details.map((detail, index) => (
                        <li key={index} className="text-gray-700 flex items-start gap-3 text-base leading-relaxed">
                          <span className="text-blue-500 mt-1 text-lg">•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Vocabulary Words */}
                  <div>
                    <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2 text-lg">
                      <Sparkles className="h-5 w-5" />
                      Helpful Words:
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {suggestions.vocabulary.map((word, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="bg-green-100 text-green-700 border-green-300 cursor-pointer hover:bg-green-200 transition-colors px-3 py-1 text-sm font-medium"
                          onClick={() => {
                            navigator.clipboard.writeText(word);
                          }}
                        >
                          {word}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-3">
                      💡 Click on any word to copy it!
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-base">Unable to load suggestions at the moment.</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={loadSuggestions}
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {/* Writing Tip */}
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 font-medium text-base">
                  💡 <strong>Tip:</strong> Read your story out loud to see if it sounds good!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WritingSuggestionsPopover;
